<template>
  <div class="rso-node-wrap">
    <div
      class="rso-node-row"
      :class="['rso-row', `rso-row--${node.node_type}`]"
    >
      <table class="rso-node-row-table rso-mini-table rso-smeta-grid-table" lang="ru">
        <colgroup>
          <col
            v-for="c in USP_MATERIALS_NDS_GRID_COLS"
            :key="`nc-${c.key}`"
            :style="{ width: c.width }"
          />
        </colgroup>
        <tbody>
          <tr>
            <td
              class="rso-node-row-td rso-node-row-td--label tw-text-left"
              :style="{ paddingLeft: `${padPx}px` }"
            >
              <div class="rso-node-row-label-inner">
                <button
                  v-if="canExpand"
                  type="button"
                  class="rso-expand-btn"
                  :aria-expanded="isOpen ? 'true' : 'false'"
                  @click="emit('toggle', node.id)"
                >
                  <span class="rso-expand-icon">{{ isOpen ? "▾" : "▸" }}</span>
                </button>
                <span v-else class="rso-expand-spacer" />
                <q-icon
                  v-if="node.node_type === 'resident'"
                  name="apartment"
                  size="13px"
                  class="rso-icon"
                />
                <q-icon
                  v-else-if="node.node_type === 'project'"
                  name="account_tree"
                  size="13px"
                  class="rso-icon"
                />
                <q-icon
                  v-else-if="node.node_type === 'konstruktiv' && node.unmapped_reason"
                  name="link_off"
                  size="13px"
                  class="rso-icon rso-icon--unmapped"
                />
                <span
                  class="rso-node-label"
                  :class="`rso-node-label--${node.node_type}`"
                >
                  {{ node.label }}
                </span>
              </div>
            </td>
            <td class="rso-node-row-td tw-text-left">
              {{ isLeaf ? node.unit_name || "—" : "" }}
            </td>
            <td class="rso-node-row-td tw-text-right">
              <template v-if="isLeaf">{{ formatNum(node.qty, 3) }}</template>
            </td>
            <td class="rso-node-row-td tw-text-right">
              <Amount v-if="isLeaf && hasNum(node.price_with_nds)" :value="node.price_with_nds" :round="2" />
            </td>
            <td class="rso-node-row-td tw-text-right rso-node-row-td--cost">
              <Amount v-if="hasNum(node.sum_with_nds)" :value="node.sum_with_nds" :round="2" />
              <span v-else class="rso-empty">—</span>
            </td>
            <td class="rso-node-row-td tw-text-right">
              <Amount v-if="isLeaf && hasNum(node.price_without_nds)" :value="node.price_without_nds" :round="2" />
            </td>
            <td class="rso-node-row-td tw-text-right rso-node-row-td--cost">
              <Amount v-if="hasNum(node.sum_without_nds)" :value="node.sum_without_nds" :round="2" />
              <span v-else class="rso-empty">—</span>
            </td>
            <td class="rso-node-row-td tw-text-right rso-node-row-td--cost">
              <Amount v-if="hasNum(node.nds_amount)" :value="node.nds_amount" :round="2" />
              <span v-else class="rso-empty">—</span>
            </td>
            <td class="rso-node-row-td tw-text-right">
              {{ hasNum(node.nds_share_percent) ? formatNum(node.nds_share_percent, 2) : "—" }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <template v-if="isOpen">
      <UspMaterialsNdsTreeNode
        v-for="ch in node.children || []"
        :key="ch.id"
        :node="ch"
        :depth="(depth || 0) + 1"
        :expanded-ids="expandedIds"
        @toggle="emit('toggle', $event)"
      />
    </template>
  </div>
</template>

<script setup>
import { computed } from "vue";
import Amount from "@/components/common/Amount.vue";
import { USP_MATERIALS_NDS_GRID_COLS } from "./gridColumns.js";

defineOptions({ name: "UspMaterialsNdsTreeNode" });

const props = defineProps({
  node: { type: Object, required: true },
  depth: { type: Number, default: 0 },
  expandedIds: { type: Object, required: true },
});

const emit = defineEmits(["toggle"]);

const isLeaf = computed(() => props.node.node_type === "material");
const canExpand = computed(
  () => Array.isArray(props.node.children) && props.node.children.length > 0
);
const isOpen = computed(() => props.expandedIds.has(props.node.id));
const padPx = computed(() => (props.depth || 0) * 12 + 4);

function hasNum(v) {
  if (v == null || v === "") return false;
  return !Number.isNaN(Number(v));
}

function formatNum(v, round = 2) {
  if (!hasNum(v)) return "—";
  return Number(v).toLocaleString("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: round,
  });
}
</script>

<style scoped lang="scss">
.rso-node-wrap {
  width: 100%;
}

.rso-node-row {
  border-bottom: 1px solid #b8cfe4;
}

.rso-node-row-table {
  width: 100%;
}

.rso-node-row-label-inner {
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 24px;
}

.rso-expand-btn {
  border: 0;
  background: transparent;
  padding: 0;
  width: 18px;
  cursor: pointer;
  color: #334155;
}

.rso-expand-spacer {
  display: inline-block;
  width: 18px;
}

.rso-icon {
  color: #5b8fce;
  flex-shrink: 0;
}

.rso-icon--unmapped {
  color: #c2410c;
}

.rso-node-label {
  font-size: 12px;
  color: #0f172a;
  word-break: break-word;
}

.rso-node-label--resident,
.rso-node-label--project,
.rso-node-label--konstruktiv {
  font-weight: 600;
}

.rso-row--resident .rso-node-row-table {
  background: #e9f1fb;
}

.rso-row--project .rso-node-row-table {
  background: #eef2ff;
}

.rso-row--konstruktiv .rso-node-row-table {
  background: #fff;
}

.rso-row--material .rso-node-row-table {
  background: #fff;
}

.rso-empty {
  color: #94a3b8;
}

.rso-node-row-td--cost .amount {
  font-weight: 600;
}
</style>
